import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, Briefcase } from "lucide-react";
import type { Candidate } from "../types";
import { useJobList } from "@/hooks/useJobList";

interface CandidateInsightsProps {
  candidate: Candidate;
}

export const CandidateInsights = ({ candidate }: CandidateInsightsProps) => {
  const { data: jobs = [] } = useJobList();
  
  // Calculate job matches based on skills
  const jobMatches = jobs.map(job => {
    // Jobs from the database might not have a skills property, define default skills for matching
    // Either use skills from the job or an empty array if not defined
    const requiredSkills = job.skills || [];
    const candidateSkills = candidate.skills || [];
    
    // Calculate match percentage based on overlapping skills
    const matchingSkills = requiredSkills.filter(skill => 
      candidateSkills.some(candSkill => 
        candSkill.toLowerCase().includes(skill.toLowerCase())
      )
    );
    
    const matchPercentage = requiredSkills.length 
      ? (matchingSkills.length / requiredSkills.length) * 100
      : 0;

    return {
      jobTitle: job.title,
      matchPercentage: Math.round(matchPercentage)
    };
  }).sort((a, b) => b.matchPercentage - a.matchPercentage);

  // This would normally come from an API/database, using mock data for now
  const skills = [
    { name: "React.js", proficiency: 95, level: "Expert" },
    { name: "Node.js", proficiency: 82, level: "Advanced" },
    { name: "TypeScript", proficiency: 78, level: "Advanced" },
    { name: "AWS", proficiency: 65, level: "Intermediate" },
    { name: "Python", proficiency: 45, level: "Beginner" }
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-primary" />
            Job Match Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {jobMatches.slice(0, 5).map((match) => (
              <div key={match.jobTitle}>
                <div className="flex justify-between mb-1">
                  <div className="text-sm font-medium truncate max-w-[70%]">
                    {match.jobTitle}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {match.matchPercentage}% Match
                  </div>
                </div>
                <Progress 
                  value={match.matchPercentage} 
                  className="h-2"
                  indicatorClassName={
                    match.matchPercentage >= 80 ? "bg-green-500" :
                    match.matchPercentage >= 60 ? "bg-yellow-500" :
                    "bg-orange-500"
                  }
                />
              </div>
            ))}
            {jobMatches.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No open jobs available for matching
              </p>
            )}
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Resume Analysis</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
              <Upload className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-1">Upload Resume</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Drop your file here or click to browse
            </p>
            <Button>Select File</Button>
          </div>
          <div className="text-sm text-muted-foreground">
            Supported formats: PDF, DOCX, TXT (Max 5MB)
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Skills Assessment</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {skills.map((skill) => (
              <div key={skill.name}>
                <div className="flex justify-between mb-1">
                  <div className="text-sm font-medium">{skill.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {skill.level} ({skill.proficiency}%)
                  </div>
                </div>
                <Progress value={skill.proficiency} className="h-2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Candidate Recommendation Report</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium mb-2">Summary</h3>
              <p className="text-muted-foreground">
                {candidate.first_name} {candidate.last_name} is a qualified candidate with relevant experience in software development.
                Their background in {candidate.skills?.join(", ")} makes them a potential match for various positions.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-2">Development Areas</h3>
              <ul className="space-y-1 text-muted-foreground">
                <li>Areas for growth will be identified through further assessment</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
